import ErrorToast from '@/common/components/toast/ErrorToast.vue';
import InfoToast from '@/common/components/toast/InfoToast.vue';
import PersistentErrorToast from '@/common/components/toast/PersistentErrorToast.vue';
import SuccessToast from '@/common/components/toast/SuccessToast.vue';
import WarningToast from '@/common/components/toast/WarningToast.vue';
import { h, markRaw } from 'vue';
import { toast } from 'vue-sonner';

type ToastPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

type ToastType = 'success' | 'error' | 'warning' | 'info';

const DEFAULT_DURATION = 9000;
const DEFAULT_POSITION: ToastPosition = 'top-right';

/**
 * @author Omar Sánchez Martínez
 * @since 02/08/2025
 * @description Composable for managing toast notifications with modern design
 */
export const useToasts = () => {
  const showToast = (
    title: string,
    message: string,
    type: ToastType = 'success',
    position: ToastPosition = DEFAULT_POSITION,
    duration: number = DEFAULT_DURATION,
  ) => {
    const toastComponents = {
      success: markRaw(SuccessToast),
      error: markRaw(ErrorToast),
      warning: markRaw(WarningToast),
      info: markRaw(InfoToast),
    };

    const ToastComponent = toastComponents[type];

    const toastId = toast.custom(
      h(
        ToastComponent,
        {
          title,
          onClose: () => {
            toast.dismiss(toastId);
          },
        },
        () => message,
      ),
      {
        duration,
        position,
      },
    );
  };

  const successToast = (
    title: string,
    message: string,
    position: ToastPosition = DEFAULT_POSITION,
    duration: number = DEFAULT_DURATION,
  ) => {
    showToast(title, message, 'success', position, duration);
  };

  const errorToast = (
    title: string,
    message: string,
    position: ToastPosition = DEFAULT_POSITION,
    duration: number = DEFAULT_DURATION,
  ) => {
    showToast(title, message, 'error', position, duration);
  };

  const warningToast = (
    title: string,
    message: string,
    position: ToastPosition = DEFAULT_POSITION,
    duration: number = DEFAULT_DURATION,
  ) => {
    showToast(title, message, 'warning', position, duration);
  };

  const infoToast = (
    title: string,
    message: string,
    position: ToastPosition = DEFAULT_POSITION,
    duration: number = DEFAULT_DURATION,
  ) => {
    showToast(title, message, 'info', position, duration);
  };

  const persistentErrorToast = (
    title: string,
    message: string,
    duration: number = 20000, // Default 20 seconds for persistent toast
    icon?: string,
  ) => {
    const toastId = toast.custom(
      h(markRaw(PersistentErrorToast), {
        title,
        message,
        duration,
        icon,
        onClose: () => {
          toast.dismiss(toastId);
        },
      }),
      {
        duration,
        position: 'bottom-center',
      },
    );
  };

  return {
    showToast,
    successToast,
    errorToast,
    warningToast,
    infoToast,
    persistentErrorToast,
  };
};
